'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Sidebar, { SidebarItem } from '@/components/layout/Sidebar';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { getDashboardRouteForRole } from '@/lib/role-routes';
import { startVisibilityAwarePolling } from '@/lib/polling';
import { gradeToAssessmentType } from '@/lib/career-assessment';

const studentNavItems: SidebarItem[] = [
  {
    label: 'Dashboard',
    href: '/student/dashboard',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:17:6" data-component-path="src/app/student/layout.tsx" data-component-line="17" data-component-end-line="24" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:18:8" data-component-path="src/app/student/layout.tsx" data-component-line="18" data-component-end-line="23" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    label: 'My Requests',
    href: '/student/requests',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:31:6" data-component-path="src/app/student/layout.tsx" data-component-line="31" data-component-end-line="38" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:32:8" data-component-path="src/app/student/layout.tsx" data-component-line="32" data-component-end-line="37" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    label: 'Messages',
    href: '/student/messages',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:45:6" data-component-path="src/app/student/layout.tsx" data-component-line="45" data-component-end-line="52" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:46:8" data-component-path="src/app/student/layout.tsx" data-component-line="46" data-component-end-line="51" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
        />
      </svg>
    ),
  },
  {
    label: 'Meetings',
    href: '/student/meetings',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:59:6" data-component-path="src/app/student/layout.tsx" data-component-line="59" data-component-end-line="66" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:60:8" data-component-path="src/app/student/layout.tsx" data-component-line="60" data-component-end-line="65" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Guidance',
    href: '/student/guidance',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:73:6" data-component-path="src/app/student/layout.tsx" data-component-line="73" data-component-end-line="80" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:74:8" data-component-path="src/app/student/layout.tsx" data-component-line="74" data-component-end-line="79" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
  {
    label: 'Surveys',
    href: '/student/surveys',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:87:6" data-component-path="src/app/student/layout.tsx" data-component-line="87" data-component-end-line="94" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:88:8" data-component-path="src/app/student/layout.tsx" data-component-line="88" data-component-end-line="93" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Career Test',
    href: '/student/career-assessment',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:101:6" data-component-path="src/app/student/layout.tsx" data-component-line="101" data-component-end-line="109" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:102:8" data-component-path="src/app/student/layout.tsx" data-component-line="102" data-component-end-line="102" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
        <path data-component-id="src/app/student/layout.tsx:103:8" data-component-path="src/app/student/layout.tsx" data-component-line="103" data-component-end-line="108" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
        />
      </svg>
    ),
  },
  {
    label: 'AI Tools',
    href: '/student/tools',
    icon: (
      <svg data-component-id="src/app/student/layout.tsx:116:6" data-component-path="src/app/student/layout.tsx" data-component-line="116" data-component-end-line="123" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/student/layout.tsx:117:8" data-component-path="src/app/student/layout.tsx" data-component-line="117" data-component-end-line="122" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
  },
];

export default function StudentLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

  const isApproved = user?.approved === true;
  const shouldPauseBadgePolling =
    pathname.startsWith('/student/messages') ||
    pathname.startsWith('/student/requests') ||
    pathname.startsWith('/student/dashboard');

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      router.push('/auth/login');
      return;
    }

    if (user.role !== 'student') {
      router.push(getDashboardRouteForRole(user.role));
    }
  }, [user, isLoading, router]);

  // Redirect unapproved students to dashboard
  useEffect(() => {
    if (!isLoading && user && user.role === 'student' && !isApproved && pathname !== '/student/dashboard') {
      router.push('/student/dashboard');
    }
  }, [isLoading, user, isApproved, pathname, router]);

  // Compute badge counts for nav items
  const computeBadges = useCallback(async () => {
    if (!user || !isApproved) return;

    const counts: Record<string, number> = {};

    const { data: responseRequests } = await supabase
      .from('requests')
      .select('id')
      .eq('student_id', user.id)
      .not('response', 'is', null)
      .neq('status', 'pending');

    if ((responseRequests || []).length > 0) {
      counts['/student/requests'] = responseRequests!.length;
    }

    const { data: counselorRows, error: counselorsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('school_id', user.schoolId)
      .eq('role', 'counselor')
      .eq('approved', true);

    if (counselorsError) {
      setBadgeCounts(counts);
      return;
    }

    const counselorIds = (counselorRows || []).map((row) => row.id);
    if (counselorIds.length > 0) {
      const keys = counselorIds.map((counselorId) => [user.id, counselorId].sort().join('__'));
      const [{ data: messageRows }, { data: readRows }] = await Promise.all([
        supabase
          .from('messages')
          .select('conversation_key,sender_role,created_at')
          .in('conversation_key', keys)
          .eq('sender_role', 'counselor'),
        supabase
          .from('message_reads')
          .select('conversation_key,last_read_at')
          .eq('reader_id', user.id)
          .in('conversation_key', keys),
      ]);

      const readByConversation = new Map<string, string>();
      (readRows || []).forEach((row) => {
        readByConversation.set(row.conversation_key, row.last_read_at);
      });

      const totalUnread = (messageRows || []).reduce((sum, row) => {
        const lastReadAt = readByConversation.get(row.conversation_key);
        if (!lastReadAt || new Date(row.created_at).getTime() > new Date(lastReadAt).getTime()) {
          return sum + 1;
        }
        return sum;
      }, 0);

      if (totalUnread > 0) counts['/student/messages'] = totalUnread;
    }

    const [{ data: surveyRows }, { data: surveyReceiptRows }] = await Promise.all([
      supabase.from('surveys').select('id').eq('school_id', user.schoolId).eq('status', 'published'),
      supabase.from('survey_receipts').select('survey_id').eq('respondent_id', user.id),
    ]);
    const respondedSurveyIds = new Set((surveyReceiptRows || []).map((row) => row.survey_id));
    const pendingSurveys = (surveyRows || []).filter((row) => !respondedSurveyIds.has(row.id)).length;
    if (pendingSurveys > 0) counts['/student/surveys'] = pendingSurveys;

    const assessmentType = gradeToAssessmentType(user.gradeLevel);
    if (assessmentType) {
      const { data: assessmentRow } = await supabase
        .from('career_assessment_results')
        .select('id')
        .eq('student_id', user.id)
        .eq('assessment_type', assessmentType)
        .maybeSingle();
      if (!assessmentRow) counts['/student/career-assessment'] = 1;
    }

    setBadgeCounts(counts);
  }, [user, isApproved]);

  useEffect(() => {
    if (!user?.id || !isApproved) return;

    void computeBadges();
    if (shouldPauseBadgePolling) return;

    return startVisibilityAwarePolling(() => computeBadges(), 20000);
  }, [user?.id, isApproved, computeBadges, shouldPauseBadgePolling]);

  if (isLoading) {
    return (
      <div data-component-id="src/app/student/layout.tsx:254:6" data-component-path="src/app/student/layout.tsx" data-component-line="254" data-component-end-line="259" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <div data-component-id="src/app/student/layout.tsx:255:8" data-component-path="src/app/student/layout.tsx" data-component-line="255" data-component-end-line="258" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/app/student/layout.tsx:256:10" data-component-path="src/app/student/layout.tsx" data-component-line="256" data-component-end-line="256" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20border-4%20border-primary%20border-t-transparent%20rounded-full%20animate-spin%20mx-auto%20mb-4%22%7D" className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p data-component-id="src/app/student/layout.tsx:257:10" data-component-path="src/app/student/layout.tsx" data-component-line="257" data-component-end-line="257" data-component-file="layout.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Loading...%22%7D" className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'student') {
    return null;
  }

  const visibleNavItems = isApproved
    ? studentNavItems.map(item => ({
        ...item,
        badge: badgeCounts[item.href] || undefined,
      }))
    : studentNavItems.filter(item => item.href === '/student/dashboard');

  return (
    <div data-component-id="src/app/student/layout.tsx:275:4" data-component-path="src/app/student/layout.tsx" data-component-line="275" data-component-end-line="288" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Sidebar data-component-id="src/app/student/layout.tsx:276:6" data-component-path="src/app/student/layout.tsx" data-component-line="276" data-component-end-line="284" data-component-file="layout.tsx" data-component-name="Sidebar" data-component-content="%7B%22elementName%22%3A%22Sidebar%22%7D"
        items={visibleNavItems}
        userType="student"
        userName={`${user.firstName} ${user.lastName}`}
        userEmail={user.email}
        userAvatar={user.profileImage}
        userSchool={user.schoolName}
        userGrade={user.gradeLevel}
      />
      <main data-component-id="src/app/student/layout.tsx:285:6" data-component-path="src/app/student/layout.tsx" data-component-line="285" data-component-end-line="287" data-component-file="layout.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22lg%3Apl-64%20min-h-screen%22%7D" className="lg:pl-64 min-h-screen">
        <div data-component-id="src/app/student/layout.tsx:286:8" data-component-path="src/app/student/layout.tsx" data-component-line="286" data-component-end-line="286" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20sm%3Ap-6%20lg%3Ap-8%20pt-20%20lg%3Apt-8%22%7D" className="p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">{children}</div>
      </main>
    </div>
  );
}
